import Model from './Model'
import React from "react";

export default class SystemNotification extends Model
{
    static Key = 'SystemNotification'

    // ─── Categories ───────────────────────────────────────────────────────────

    static CATEGORIES = [
        {value: 1, label: 'Announcement',                      cls: 'bg-violet-100 text-violet-700'},
        {value: 2, label: 'Potential Talent Matches for Role',  cls: 'bg-amber-100 text-amber-700'},
        {value: 3, label: 'Shortlisted Candidates for Role',    cls: 'bg-blue-100 text-blue-700'},
    ]

    // Categories that require a client + position to be selected
    static CLIENT_CATEGORIES = [2, 3]

    static category(value) {
        return this.CATEGORIES.find(c => c.value === value)
            ?? {value, label: `Type ${value}`, cls: 'bg-gray-100 text-gray-600'}
    }

    // ─── Actions ──────────────────────────────────────────────────────────────

    static ACTIONS = [
        {label: 'No Action (Default)', value: null},
        {label: 'Project', value: 1},
        {label: 'Edit Availability', value: 2},
        {label: 'Edit Profile', value: 3},
        {label: 'View Opportunities', value: 4},
        {label: 'View Job Board', value: 5},
    ]

    static action(value, category) {

        switch (value) {
            case null:
                return {value, label: `No Action (Default)`, cls: 'bg-gray-100 text-gray-500'}
            case 1:
                return {value, label: category === 2 ? 'Job Board Details' : 'Opportunity Dashboard', cls: 'bg-amber-100 text-amber-700'}
            default:
                return {value, label: TB.find(this.ACTIONS, ['value', value])?.label, cls: 'bg-purple-100 text-purple-600'}
        }
    }

    // ─── Disciplines ─────────────────────────────────────────────────────────
    // Flat id+name list built once from the Laravel injection

    static _allDisciplines = null

    static allDisciplines() {
        if (!this._allDisciplines) {
            this._allDisciplines = (laravel.discipline_categories ?? []).flatMap(
                cat => cat.disciplines.map(d => ({id: d.id, name: d.name}))
            )
        }
        return this._allDisciplines
    }

    static disciplineName(id) {
        return this.allDisciplines().find(d => d.id === id)?.name ?? null
    }

    // Shows first 2 discipline names; appends "and N more" when there are more than 2
    static formatDisciplineNames(ids) {
        const names = ids.map(id => this.disciplineName(id)).filter(Boolean)
        if (names.length <= 2) return names.join(', ')
        return `${names[0]}, ${names[1]}, and ${names.length - 2} more`
    }

    // ─── Audience label ───────────────────────────────────────────────────────
    // Returns a human-readable summary of who a notification was sent to

    static audienceLabel({category, filters}) {
        if (category === 2) return 'Role Talent Matches'
        if (category === 3) return 'Shortlisted Candidates'

        // Category 1 — Announcement
        if (!filters) return 'All Talent'


        let locationSuffix = '';
        if (filters.visibility_setting_location === 2) {
            let regionSuffix = '';
            if (filters.regions?.length) {
                let matchedRegions = TB.filter(laravel.regions, region => filters.regions?.includes(region.id));
                if (matchedRegions.length > 0) {
                    regionSuffix = TB.map(matchedRegions, 'name').join(', ')
                }
            }

            locationSuffix = ` in the ${regionSuffix} ${TB.pluralize('region', filters.regions?.length, false)}`;
        } else if (filters.visibility_setting_location >= 4) {
            locationSuffix = ` ${laravel.project_visibility_location_settings[filters.visibility_setting_location]?.toLowerCase()} of ${filters.address}`;
        }

        if (filters.only_user_ids?.length > 0) {
            return 'Specific talent';
        } else if (filters.discipline_ids?.length > 0) {
            return `${this.formatDisciplineNames(filters.discipline_ids)}${locationSuffix}`;
        } else if (locationSuffix) {
            return `Talent${locationSuffix}`;
        }
        return 'All Talent'
    }
}